<script setup lang="ts">
import { onMounted, shallowRef, ref } from "vue";
import { AgGridVue } from "ag-grid-vue3";
import {
  themeQuartz,
  type CellValueChangedEvent,
  type GridApi,
  type GridOptions,
  type GridReadyEvent,
} from "ag-grid-enterprise";
import {useCharterClientsStore} from "@/charter/stores/charter-clients-store.ts";
import {Client, ClientCreateRequest, ClientUpdateRequest} from "@/api/client/models/index.ts";

const clientsStore = useCharterClientsStore();

const myTheme = themeQuartz.withParams({});

const gridOptions: GridOptions = {
  pagination: true,
  singleClickEdit: false,
  autoSizeStrategy: {
    type: "fitGridWidth",
    defaultMinWidth: 100,
  },
  defaultColDef: {
    resizable: false,
    filter: true,
  },
  columnDefs: [
    { field: "id", headerName: "ID" },
    { field: "name", headerName: "Name", editable: true },
    { field: "acBase", headerName: "Aircraft Base", editable: true },
    { field: "sapNumber", headerName: "SAP Number", editable: true },
    { field: "rangeFlights", headerName: "Range Flights", editable: true },
    {
      field: "isVip",
      headerName: "VIP",
      editable: true,
      singleClickEdit: true,
      cellDataType: "boolean",
      cellEditor: "agCheckboxCellEditor",
      cellRenderer: "agCheckboxCellRenderer",
    },
    { field: "isActive", headerName: "Active" },
  ],
};

const gridApi = shallowRef<GridApi | null>(null);
const showAddForm = ref(false);
const newClient = ref<ClientCreateRequest>({
  name: "",
  acBase: "",
  sapNumber: "",
  rangeFlights: "",
  isVip: false,
  isActive: true
});
const isSubmitting = ref(false);
const formError = ref<string | null>(null);
const gridError = ref<string | null>(null);

onMounted(async () => {
  await clientsStore.fetchClients();
});

function onGridReady(params: GridReadyEvent) {
  gridApi.value = params.api;
}

async function onCellValueChanged(event: CellValueChangedEvent<Client>) {
  const field = event.colDef.field;

  if (!field || event.newValue === event.oldValue || !event.data?.id) {
    return;
  }

  const clientUpdateRequest: ClientUpdateRequest = {
    id: event.data.id,
    version: event.data.version,
    isActive: event.data.isActive,
    name: event.data.name,
    acBase: event.data.acBase,
    sapNumber: event.data.sapNumber,
    rangeFlights: event.data.rangeFlights,
    isVip: event.data.isVip ?? false,
  };

  try {
    gridError.value = null;
    await clientsStore.updateClient(clientUpdateRequest);
  } catch (error) {
    gridError.value = error instanceof Error ? error.message : "Failed to update client";
    await clientsStore.fetchClients();
  }
}

async function refreshData() {
  await clientsStore.fetchClients();
}

function toggleAddForm() {
  showAddForm.value = !showAddForm.value;
  if (showAddForm.value) {
    // Reset form when opening
    newClient.value = {
      name: "",
      acBase: "",
      sapNumber: "",
      rangeFlights: "",
      isVip: false,
      isActive: true
    };
    formError.value = null;
  }
}

async function submitNewClient() {
  if (!newClient.value.name || !newClient.value.acBase || !newClient.value.rangeFlights) {
    formError.value = "Please fill in all required fields";
    return;
  }

  try {
    isSubmitting.value = true;
    formError.value = null;
    await clientsStore.addClient(newClient.value);
    // Reset form after successful submission
    newClient.value = {
      name: "",
      acBase: "",
      sapNumber: "",
      rangeFlights: "",
      isVip: false,
      isActive: true
    };
    // Close the form
    showAddForm.value = false;
  } catch (error) {
    formError.value = error instanceof Error ? error.message : "Failed to add client";
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <div class="flex h-full flex-col gap-4">
    <div class="flex items-center justify-between">
      <h2>Clients</h2>
      <div class="flex gap-2">
        <p-button label="Add Client" icon="pi pi-plus" severity="primary" outlined @click="toggleAddForm" />
        <p-button label="Refresh" icon="pi pi-refresh" severity="secondary" outlined @click="refreshData" />
      </div>
    </div>

    <!-- Add Client Form -->
    <div v-if="showAddForm" class="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <h3 class="mb-3 mt-0">Add New Client</h3>
      <div v-if="formError" class="mb-3 rounded-md border-l-4 border-red-500 bg-red-50 p-2 text-sm text-red-500">{{ formError }}</div>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
        <div class="space-y-2">
          <label for="name" class="block mb-2">Name*</label>
          <p-input-text id="name" v-model="newClient.name" class="w-full" :disabled="isSubmitting" />
        </div>
        <div class="space-y-2">
          <label for="acBase" class="block mb-2">Aircraft Base*</label>
          <p-input-text id="acBase" v-model="newClient.acBase" class="w-full" :disabled="isSubmitting" />
        </div>
        <div class="space-y-2">
          <label for="sapNumber" class="block mb-2">SAP Number</label>
          <p-input-text id="sapNumber" v-model="newClient.sapNumber" class="w-full" :disabled="isSubmitting" />
        </div>
        <div class="space-y-2">
          <label for="rangeFlights" class="block mb-2">Range Flights*</label>
          <p-input-text id="rangeFlights" v-model="newClient.rangeFlights" class="w-full" :disabled="isSubmitting" />
        </div>
        <div class="space-y-2">
          <label for="isVip" class="block mb-2">VIP</label>
          <p-checkbox id="isVip" v-model="newClient.isVip" :binary="true" :disabled="isSubmitting" />
        </div>
      </div>
      <div class="mt-3 flex justify-end gap-2">
        <p-button label="Cancel" severity="secondary" outlined @click="toggleAddForm" :disabled="isSubmitting" />
        <p-button label="Save" icon="pi pi-save" @click="submitNewClient" :loading="isSubmitting" />
      </div>
    </div>

    <div v-if="gridError" class="rounded-md border-l-4 border-red-500 bg-red-50 p-2 text-sm text-red-500">
      {{ gridError }}
    </div>

    <AgGridVue
      :rowData="clientsStore.clients"
      :loading="clientsStore.isLoading"
      :gridOptions="gridOptions"
      :theme="myTheme"
      class="ag-grid-container"
      @cell-value-changed="onCellValueChanged"
      @grid-ready="onGridReady"
    />
  </div>
</template>

<style scoped lang="scss">
.ag-grid-container {
  width: 100%;
  height: 400px;
}

h2, h3 {
  margin: 0 !important;
}

.p-button {
  min-height: fit-content !important;
}

.p-button-icon-left {
  margin-right: 0.4375rem !important;
}

.p-button-label {
  font-weight: 700 !important;
}

.p-button-outlined {
  background-color: white !important;
  color: rgb(30, 41, 59) !important;
  font-size: 14px;
  line-height: 21px !important;
  padding: 6px 10px;
  border-radius: 6px;
  border-color: #e2e8f0 !important;
}

.p-button-outlined:hover {
  background-color: var(--p-button-outlined-secondary-hover-background) !important;
}

.p-button-outlined:active {
  background-color: var(--p-button-outlined-secondary-active-background) !important;
}

label {
  font-weight: 500;
  color: var(--text-color-secondary);
}
</style>
